import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextStandardTeaser from '@contexts/standardTeaser'
import { StandardTeaserLayoutContextType } from '@contexts/standardTeaser'
import { LogoProps } from '@widgets/Teaser/standard/types'
import FreeRatioImage from '@components/FreeRatioImage'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'

type StyledLogoProps = Pick<
  StandardTeaserLayoutContextType,
  'teaserFlex' | 'isInMegaModule'
> &
  Pick<LogoProps, 'isOnImage'>

const { StandardTeaserLayoutContext } = contextStandardTeaser

const StyledLogo = styled.div<StyledLogoProps>`
  ${({ teaserFlex, isOnImage, isInMegaModule }) => {
    const basicCSS = css`
      width: auto;
      height: auto;
    `
    const notOnImageCSS = css`
      margin-left: 4px;
    `
    const hideCSS = css`
      display: none;
    `

    const imageSizeTablet = css`
      max-width: 45px;
      max-height: 38px;
    `
    const imageSizeMobile = css`
      max-width: 45px;
      max-height: 20px;
    `

    let cssDesktop
    if (isInMegaModule) {
      if (isOnImage) {
        cssDesktop = css`
          ${hideCSS}
        `
      } else {
        cssDesktop = css`
          ${basicCSS}
          ${notOnImageCSS}
            ${imageSizeMobile}
        `
      }
    } else {
      if (isOnImage) {
        let maxWidth
        let maxHeight
        if (teaserFlex === '3') {
          maxWidth = '60px'
          maxHeight = '50px'
        } else {
          maxWidth = '45px'
          maxHeight = '38px'
        }
        cssDesktop = css`
          ${basicCSS}
          max-width: ${maxWidth};
          max-height: ${maxHeight};
        `
      } else {
        cssDesktop = css`
          ${hideCSS}
        `
      }
    }

    let cssTablet
    if (isInMegaModule) {
      if (isOnImage) {
        cssTablet = css`
          ${hideCSS}
        `
      } else {
        cssTablet = css`
          ${basicCSS}
          ${notOnImageCSS}
            ${imageSizeTablet}
        `
      }
    } else {
      if (isOnImage) {
        cssTablet = css`
          ${basicCSS}
          ${imageSizeTablet}
        `
      } else {
        cssTablet = css`
          ${hideCSS}
        `
      }
    }

    let cssMobile
    if (isOnImage) {
      cssMobile = css`
        ${hideCSS}
      `
    } else {
      cssMobile = css`
        ${basicCSS};
        ${notOnImageCSS}
        ${imageSizeMobile}
      `
    }

    return css`
      ${desktopCSS(css`
        ${cssDesktop}
      `)}

      ${tabletCSS(css`
        ${cssTablet}
      `)}

      ${mobileCSS(css`
        ${cssMobile}
      `)}
    `
  }}
`

const Logo: FunctionComponent<LogoProps> = ({
  src,
  alt,
  width,
  height,
  crops,
  isOnImage,
}) => {
  const { teaserFlex, isInMegaModule } = useContext(StandardTeaserLayoutContext)

  const maxWidthMobile = 45
  const maxHeightMobile = 20

  let maxWidthDesktop = 0
  let maxHeightDesktop = 0
  if (teaserFlex === '3') {
    maxWidthDesktop = isInMegaModule ? maxWidthMobile : 60
    maxHeightDesktop = isInMegaModule ? maxHeightMobile : 50
  } else {
    maxWidthDesktop = isInMegaModule ? maxWidthMobile : 45
    maxHeightDesktop = isInMegaModule ? maxHeightMobile : 38
  }

  const maxWidthTablet = isInMegaModule ? maxWidthMobile : 45
  const maxHeightTablet = isInMegaModule ? maxHeightMobile : 38

  return (
    <StyledLogo
      isOnImage={isOnImage}
      isInMegaModule={isInMegaModule}
      teaserFlex={teaserFlex}>
      <FreeRatioImage
        src={src}
        alt={alt ?? ''}
        originalDimensions={{ width, height }}
        constraints={{
          desktop: { width: maxWidthDesktop, height: maxHeightDesktop },
          tablet: { width: maxWidthTablet, height: maxHeightTablet },
          mobile: { width: maxWidthMobile, height: maxHeightMobile },
          mobileSmall: { width: maxWidthMobile, height: maxHeightMobile },
        }}
        crops={crops ?? {}}
      />
    </StyledLogo>
  )
}

export default Logo
